import {
  Commitment,
  Relationship,
} from "@/services/backend/relationships/types/relationship";
import { Dialog, DialogContent } from "@/shared/components/ui/dialog";
import {
  CommonDialogFooter,
  CommonDialogHeader,
  CommonDialogTrigger,
} from "@/shared/components/dialog/dialog-common";
import t from "@/lang/lang";
import { Plus } from "lucide-react";
import {
  CreateRequest,
  useRelationshipsFulfillmentCreateMutation,
} from "@/services/backend/relationships/api/fulfillment_service";
import { v4 } from "uuid";
import { ReactNode, useState } from "react";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { Label as StateLabel } from "@/services/backend/processes/api/state";
import { ProcessSelect } from "@/services/backend/processes/components/process-select";
import { Label } from "@/shared/components/ui/label";
import { DateInput } from "@/shared/components/form/date-input";
import {
  inputValidationErrorMessages,
  InputValidationErrors,
} from "@/shared/components/errors/input-error-messages";

export function CreateFulfillmentDialog({
  relationship,
  commitment,
  trigger,
}: {
  relationship: Relationship;
  commitment: Commitment;
  trigger?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [request, setRequest] = useState<CreateRequest>({
    id: v4(),
    companyId: relationship.companyId,
    branchId: relationship.branchId,
    relationshipId: relationship.id,
    commitmentId: commitment.id,
    processId: commitment.processId ?? undefined,
    serviceKind: commitment.serviceKind,
    plannedDate: new Date().toISOString(),
  });
  const [post, { isLoading, error, isSuccess, reset }] =
    useRelationshipsFulfillmentCreateMutation();

  if (isSuccess) {
    reset();
    setRequest({
      ...request,
      id: v4(),
      processId: commitment.processId ?? undefined,
      plannedDate: "",
    });
    setOpen(false);
  }

  const doCreate = () => {
    if (isLoading) return;

    post({
      ...request,
      id: v4(),
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger || (
        <CommonDialogTrigger icon={Plus} label={t("Arbeitsschein")} />
      )}
      <DialogContent>
        <CommonDialogHeader
          title={t("Arbeitsschein planen")}
          description={t(
            "Hier können Sie einen Arbeitsschein für die Sollstellung planen. Der Arbeitsschein wird dann dem ausgewählten Vorgang zugeordnet und kann dort weiter bearbeitet werden.",
          )}
        />
        <div className="grid gap-1.5">
          <Label>{t("Vorgang")}</Label>
          <ProcessSelect
            value={request.processId}
            onValueChange={(processId) => setRequest({ ...request, processId })}
            serviceKindFilter={{
              active: true,
              values: [commitment.serviceKind],
            }}
            companyFilter={{ active: true, values: [relationship.companyId] }}
            branchFilter={{ active: true, values: [relationship.branchId] }}
            customerFilter={{ active: true, values: [relationship.customerId] }}
            stateFilter={{ active: true, values: [StateLabel.Running] }}
          />
          <InputValidationErrors error={error} field="processId" />
        </div>
        <DateInput
          label={t("Geplante Woche")}
          date={request.plannedDate}
          onDateChange={(plannedDate) =>
            setRequest({ ...request, plannedDate: plannedDate || "" })
          }
          errors={inputValidationErrorMessages(error, "plannedDate")}
        />
        <RTKQueryErrorAlert error={error} ignoreValidationError={false} />
        <CommonDialogFooter isLoading={isLoading} onClickAction={doCreate} />
      </DialogContent>
    </Dialog>
  );
}
